/**
 * The printable schedule, as pure data (RPRR-82: "print a coach's schedule").
 *
 * The print view is a separate page (`/schedule/print`) the schedule, the itinerary panel and the
 * event view open in a new tab. It carries the coach's on-screen filters in its query string, so
 * what prints is what they were looking at, and it can be narrowed further to chosen coaches
 * ("Games assigned to: Coach A, Coach B").
 *
 * Everything here is pure: no fetches, no `new Date()` (the caller passes "now" where it matters),
 * so the rules are tested without a browser. `SchedulePrintPage` fetches and renders.
 *
 * Filters, matched to what each screen shows:
 *  - Schedule, All events: every event, and every game of every tournament, in the window.
 *  - Schedule, My events: the server's `scope=mine` read (events I am assigned to, or have a game
 *    on), then within a tournament the games that are mine or that I follow. A coach assigned to
 *    the whole tournament covers every game, so all of them print: the calendar draws that as one
 *    bar, and the printout spells the bar out game by game, which is the point of printing it. A
 *    coach on only some games gets those plus the games they follow, the calendar's `myGames`.
 *  - Itinerary panel: My schedule (`isAssignedToMe`) or All games.
 *  - Event view's games: Followed or All, with Followed decided by the same rule the tab uses
 *    (`isFollowedOnGamesTab`), so the printout never lists a game the tab hides.
 *  - Coaches, on any of them: keep a game when one of its effective coaches (tournament coaches
 *    plus the game's own) is chosen, and an event when one of its coaches is.
 */

import type { CalendarEventAssignee, CalendarEventResponse } from "../../../../../types/api";
import type {
  MatchedPlayer,
  TournamentGameDto,
  TournamentGamesResponse,
} from "../../../../../types/tournament-games";
import type { CalendarScope } from "../../../../../services/calendar";
import { dateTimeFormatter } from "../../../../../utils/dateTimeParts";
import { allEventCoaches } from "../gameCoaches";
import { eventTypeLabel } from "../eventTypeLabel";
import { gameDayKey } from "../itineraryPanelView";
import { isFollowRelevantGame, normalizeTeamName } from "../build-itinerary/itinerary";

export const SCHEDULE_PRINT_PATH = "/schedule/print";

/**
 * The longest window one printout covers. The schedule hands over the visible month, so this only
 * bites a hand-edited URL; without it, a year of "All events" would fetch every tournament's games.
 */
export const MAX_PRINT_RANGE_DAYS = 100;

/** Coaches one URL can name. More than a staff has; it only stops an absurd query string. */
const MAX_COACH_IDS = 50;

const MS_PER_DAY = 86_400_000;

/** Which of a tournament's games the event-level printouts keep. */
export type PrintGameFilter = "all" | "mine" | "followed";

const GAME_FILTERS: readonly PrintGameFilter[] = ["all", "mine", "followed"];

/**
 * What the print page was asked to print, parsed from (and written to) its query string.
 * `coachIds` is optional on the way in (the schedule's links never preselect anyone) and always
 * present once parsed.
 */
export type SchedulePrintRequest =
  | {
      kind: "range";
      /** Inclusive start, `YYYY-MM-DD`. */
      from: string;
      /** Exclusive end, `YYYY-MM-DD`. */
      to: string;
      scope: CalendarScope;
      coachIds?: readonly string[];
    }
  | {
      kind: "event";
      eventId: string;
      games: PrintGameFilter;
      coachIds?: readonly string[];
    };

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** A calendar event id. Anything else is refused before it can be put into an API path. */
const EVENT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const dayMs = (key: string): number => Date.parse(`${key}T00:00:00Z`);

/** `YYYY-MM-DD` of an instant, or "" for one that is not a date (which `isDateKey` then refuses). */
const keyFromMs = (ms: number): string =>
  Number.isFinite(ms) ? new Date(ms).toISOString().slice(0, 10) : "";

/**
 * A real `YYYY-MM-DD` day. The round trip matters: `Date.parse` rolls Feb 31 over to Mar 3 rather
 * than failing, which would fetch one window and filter by another.
 */
const isDateKey = (value: string | null): value is string =>
  value != null && DATE_KEY.test(value) && !Number.isNaN(dayMs(value)) && keyFromMs(dayMs(value)) === value;

const parseCoachIds = (raw: string | null): string[] => {
  if (!raw) return [];
  const ids = raw
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  return [...new Set(ids)].slice(0, MAX_COACH_IDS);
};

/** The print page URL for a request. Coaches are left off when none are chosen. */
export const schedulePrintUrl = (request: SchedulePrintRequest): string => {
  const params = new URLSearchParams();
  if (request.kind === "range") {
    params.set("from", request.from);
    params.set("to", request.to);
    params.set("scope", request.scope);
  } else {
    params.set("event", request.eventId);
    params.set("games", request.games);
  }
  writeCoachIds(params, request.coachIds ?? []);
  return `${SCHEDULE_PRINT_PATH}?${params.toString()}`;
};

/** Writes the chosen coaches into a query string, dropping the parameter when there are none. */
export const writeCoachIds = (params: URLSearchParams, coachIds: readonly string[]): void => {
  if (coachIds.length > 0) params.set("coaches", coachIds.join(","));
  else params.delete("coaches");
};

/** A parsed request: the coaches are always known, even when none are chosen. */
export type ParsedSchedulePrintRequest = SchedulePrintRequest & { coachIds: readonly string[] };

/**
 * Reads the print page's query string. Null when it names nothing printable (an event id that is
 * not an id, no valid window, an empty or backwards window, or one longer than
 * `MAX_PRINT_RANGE_DAYS`), which the page reports rather than guessing.
 */
export const parseSchedulePrintRequest = (
  params: URLSearchParams,
): ParsedSchedulePrintRequest | null => {
  const coachIds = parseCoachIds(params.get("coaches"));
  const eventId = params.get("event")?.trim();
  if (eventId !== undefined && eventId !== "") {
    if (!EVENT_ID.test(eventId)) return null;
    const games = params.get("games") as PrintGameFilter | null;
    return {
      kind: "event",
      eventId,
      games: games && GAME_FILTERS.includes(games) ? games : "all",
      coachIds,
    };
  }

  const from = params.get("from");
  const to = params.get("to");
  if (!isDateKey(from) || !isDateKey(to)) return null;
  const span = (dayMs(to) - dayMs(from)) / MS_PER_DAY;
  if (span <= 0 || span > MAX_PRINT_RANGE_DAYS) return null;
  return {
    kind: "range",
    from,
    to,
    scope: params.get("scope") === "mine" ? "mine" : "all",
    coachIds,
  };
};

/**
 * The window a schedule printout covers, as `YYYY-MM-DD` keys, from the calendar's current view.
 * FullCalendar runs in UTC here, so its view bounds are already midnight UTC of the first day and
 * of the day after the last.
 */
export const printWindowFromView = (
  currentStart: Date,
  currentEnd: Date,
): { from: string; to: string } => ({
  from: keyFromMs(currentStart.getTime()),
  to: keyFromMs(currentEnd.getTime()),
});

/** The UTC instants to fetch a `YYYY-MM-DD` window with, for `getEvents`. */
export const windowToIsoRange = (from: string, to: string): { fromIso: string; toIso: string } => ({
  fromIso: new Date(dayMs(from)).toISOString(),
  toIso: new Date(dayMs(to)).toISOString(),
});

// ---------------------------------------------------------------------------
// Game filters
// ---------------------------------------------------------------------------

/** The viewer's follows in one tournament, in the shape the game checks want. */
export interface FollowContext {
  teamIds: ReadonlySet<string>;
  /** Raw team names of name-only follows, normalized with `normalizeTeamName`. */
  teamNameKeys: ReadonlySet<string>;
}

export const followContext = (response: TournamentGamesResponse): FollowContext => ({
  teamIds: new Set(response.followedTeamIds ?? []),
  teamNameKeys: new Set((response.followedTeamNames ?? []).map(normalizeTeamName)),
});

/**
 * The event view's Followed tab: the viewer is one of the game's own coaches, a starred player
 * plays in it, or a followed team (by id) is on either side. Name-only follows do not count there.
 * The coach check reads the game's own `assignees`, not `isAssignedToMe`: that flag is true on
 * every game for a tournament-level coach, which would turn Followed into All. `TournamentGamesPanel`
 * filters with this same function, so the tab and its printout cannot disagree.
 */
export const isFollowedOnGamesTab = (
  game: Pick<TournamentGameDto, "assignees" | "matchedPlayers" | "homeTeamId" | "awayTeamId">,
  followedTeamIds: ReadonlySet<string>,
  currentUserId?: string,
): boolean =>
  (currentUserId != null && game.assignees.some((a) => a.userId === currentUserId)) ||
  game.matchedPlayers.length > 0 ||
  (game.homeTeamId != null && followedTeamIds.has(game.homeTeamId)) ||
  (game.awayTeamId != null && followedTeamIds.has(game.awayTeamId));

/** Which games of a tournament a request keeps, before the coach filter. */
export type GamePredicate = (game: TournamentGameDto, follows: FollowContext) => boolean;

export const gamePredicateFor = (
  request: SchedulePrintRequest,
  currentUserId?: string,
): GamePredicate => {
  if (request.kind === "range") {
    // "My events" also counts name-only follows, as the server's `myGames` does.
    return request.scope === "mine"
      ? (game, follows) =>
          game.isAssignedToMe ||
          isFollowRelevantGame(game, follows.teamIds, follows.teamNameKeys)
      : () => true;
  }
  switch (request.games) {
    case "mine":
      return (game) => game.isAssignedToMe;
    case "followed":
      return (game, follows) => isFollowedOnGamesTab(game, follows.teamIds, currentUserId);
    default:
      return () => true;
  }
};

const coveredBy = (
  coaches: readonly Pick<CalendarEventAssignee, "userId">[],
  coachIds: ReadonlySet<string>,
): boolean => coachIds.size === 0 || coaches.some((c) => coachIds.has(c.userId));

// ---------------------------------------------------------------------------
// Rows and days
// ---------------------------------------------------------------------------

export interface PrintGameRow {
  kind: "game";
  id: string;
  /** Start instant in ms, for ordering within the day. */
  sortKey: number;
  /** Start time in the game's own timezone, with the zone: "9:00 AM CDT". */
  time: string;
  /** The tournament the game belongs to. */
  eventTitle: string;
  home: string;
  away: string;
  field: string;
  address: string | null;
  coaches: string[];
  pitchers: string[];
  /** "Following: #12 Jane Smith (SS), ...", or null when no followed player is in the game. */
  following: string | null;
}

export interface PrintEventRow {
  kind: "event";
  id: string;
  sortKey: number;
  /** "All day", "Jul 5-7" for a multi-day event (see `allDayRangeLabel`), or "1:00 PM EDT". */
  time: string;
  title: string;
  typeLabel: string;
  location: string | null;
  address: string | null;
  coaches: string[];
}

export type PrintRow = PrintGameRow | PrintEventRow;

export interface PrintDay {
  /** `YYYY-MM-DD`, in the timezone the day's games and events are played in. */
  key: string;
  /** "Saturday, July 5, 2026". */
  label: string;
  rows: PrintRow[];
}

/** "9:00 AM CDT": the clock time where it happens, with that zone's short name. */
export const clockWithZone = (iso: string, timeZone: string): string => {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  };
  try {
    return dateTimeFormatter("en-US", { ...options, timeZone: timeZone || "UTC" }).format(at);
  } catch {
    return dateTimeFormatter("en-US", { ...options, timeZone: "UTC" }).format(at);
  }
};

const shortDate = (key: string): string =>
  dateTimeFormatter("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(dayMs(key)));

/** "Saturday, July 5, 2026" for a `YYYY-MM-DD` key. */
export const dayLabel = (key: string): string =>
  dateTimeFormatter("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(dayMs(key)));

const monthDay = (key: string): string =>
  dateTimeFormatter("en-US", { month: "short", day: "numeric", timeZone: "UTC" }).format(
    new Date(dayMs(key)),
  );

/**
 * The time cell of an all-day event, from its first and last day (`YYYY-MM-DD`, both inclusive).
 * The day heading already gives the full date, so this stays short enough for the narrow time
 * column: "All day" for one day, "Oct 17-18" within a month, "Oct 31 - Nov 2" across months, and
 * with years only when it crosses one ("Dec 30, 2026 - Jan 2, 2027").
 */
export const allDayRangeLabel = (first: string, last: string): string => {
  if (!isDateKey(first) || !isDateKey(last) || last <= first) return "All day";
  if (first.slice(0, 4) !== last.slice(0, 4)) return `${shortDate(first)} - ${shortDate(last)}`;
  if (first.slice(0, 7) === last.slice(0, 7)) return `${monthDay(first)}-${Number(last.slice(8))}`;
  return `${monthDay(first)} - ${monthDay(last)}`;
};

const eventTime = (event: CalendarEventResponse): string => {
  if (!event.isAllDay) return clockWithZone(event.startsAtUtc, event.timeZone);
  // Stored exclusive: the last day is the day before the end.
  return allDayRangeLabel(
    event.startsAtUtc.slice(0, 10),
    keyFromMs(Date.parse(event.endsAtUtc) - MS_PER_DAY),
  );
};

/** Followed players a game's line names before it says "+N more". */
export const MAX_FOLLOWING_NAMES = 8;

const jerseyOf = (p: MatchedPlayer): number | null =>
  typeof p.jerseyNumber === "number" && Number.isFinite(p.jerseyNumber) ? p.jerseyNumber : null;

/**
 * "Following: #12 Jane Smith (SS), #4 Ann Lee (P)": the starred players in a game, by jersey number
 * (no number last) then name, each once even when linked to both teams. Null when there are none,
 * so the row prints no line. Number and position are left off when the API did not send them.
 */
export const followingLine = (
  players: readonly MatchedPlayer[] | null | undefined,
): string | null => {
  const byId = new Map<string, MatchedPlayer>();
  for (const p of players ?? []) {
    if (p?.name?.trim() && !byId.has(p.prospectId)) byId.set(p.prospectId, p);
  }
  if (byId.size === 0) return null;
  const sorted = [...byId.values()].sort((a, b) => {
    const ja = jerseyOf(a);
    const jb = jerseyOf(b);
    if (ja !== jb) return ja === null ? 1 : jb === null ? -1 : ja - jb;
    return a.name.localeCompare(b.name);
  });
  const names = sorted.slice(0, MAX_FOLLOWING_NAMES).map((p) => {
    const number = jerseyOf(p);
    const position = p.position?.trim();
    return `${number === null ? "" : `#${number} `}${p.name.trim()}${position ? ` (${position})` : ""}`;
  });
  const more = sorted.length - names.length;
  return `Following: ${names.join(", ")}${more > 0 ? `, +${more} more` : ""}`;
};

const toGameRow = (event: CalendarEventResponse, game: TournamentGameDto): PrintGameRow => ({
  kind: "game",
  id: game.id,
  sortKey: Date.parse(game.startsAtUtc) || 0,
  time: clockWithZone(game.startsAtUtc, game.timeZone),
  eventTitle: event.title,
  home: game.homeTeamNameRaw.trim() || "TBD",
  away: game.awayTeamNameRaw.trim() || "TBD",
  field: game.fieldName.trim(),
  address: game.fieldAddress?.trim() || null,
  coaches: game.effectiveCoaches.map((c) => c.displayName),
  // Optional on the wire: an API older than pitcher marks leaves it off.
  pitchers: (game.pitchers ?? []).map((p) => p.name),
  following: followingLine(game.matchedPlayers),
});

const toEventRow = (event: CalendarEventResponse, dayStartMs: number): PrintEventRow => ({
  kind: "event",
  id: event.id,
  // All-day rows lead their day.
  sortKey: event.isAllDay ? dayStartMs - 1 : Date.parse(event.startsAtUtc) || dayStartMs,
  time: eventTime(event),
  title: event.title,
  typeLabel: eventTypeLabel(event.eventType),
  location: event.locationName?.trim() || null,
  address: event.locationAddress?.trim() || null,
  coaches: allEventCoaches(event).map((c) => c.displayName),
});

export interface BuildPrintDaysInput {
  events: readonly CalendarEventResponse[];
  /** Each tournament's games, by calendar event id. A tournament missing here prints as one row. */
  gamesByEvent: ReadonlyMap<string, TournamentGamesResponse>;
  predicate: GamePredicate;
  coachIds: readonly string[];
  /** The schedule window (`YYYY-MM-DD`, end exclusive). Omitted for a single tournament. */
  window?: { from: string; to: string };
}

/**
 * Lays the printout out by day.
 *
 * A tournament with games prints its games, each on the day it is played in its own timezone, and
 * not a row for the tournament itself. Anything else (a visit, a one-off, a tournament with no
 * games yet) prints as one row on the day it starts, or on the window's first day when it started
 * before the window. A game outside the window is left off: a tournament that runs across the end
 * of the month prints the games in this month only.
 */
export const buildPrintDays = ({
  events,
  gamesByEvent,
  predicate,
  coachIds,
  window,
}: BuildPrintDaysInput): PrintDay[] => {
  const chosen = new Set(coachIds);
  const byDay = new Map<string, PrintRow[]>();
  const inWindow = (key: string) =>
    key !== "" && (!window || (key >= window.from && key < window.to));
  const push = (key: string, row: PrintRow) => {
    const rows = byDay.get(key);
    if (rows) rows.push(row);
    else byDay.set(key, [row]);
  };

  for (const event of events) {
    const response = gamesByEvent.get(event.id);
    const games = event.eventType === "Tournament" ? (response?.games ?? []) : [];
    if (response && games.length > 0) {
      const follows = followContext(response);
      for (const game of games) {
        if (!predicate(game, follows) || !coveredBy(game.effectiveCoaches, chosen)) continue;
        const key = gameDayKey(game.startsAtUtc, game.timeZone);
        if (inWindow(key)) push(key, toGameRow(event, game));
      }
      continue;
    }

    if (!coveredBy(allEventCoaches(event), chosen)) continue;
    let key = event.isAllDay
      ? event.startsAtUtc.slice(0, 10)
      : gameDayKey(event.startsAtUtc, event.timeZone);
    if (window && key < window.from) key = window.from;
    if (isDateKey(key) && inWindow(key)) push(key, toEventRow(event, dayMs(key)));
  }

  return [...byDay.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, rows]) => ({
      key,
      label: dayLabel(key),
      rows: rows.sort((a, b) => a.sortKey - b.sortKey || a.id.localeCompare(b.id)),
    }));
};

// ---------------------------------------------------------------------------
// Header copy and the coach picker
// ---------------------------------------------------------------------------

export interface PrintCoachOption {
  userId: string;
  displayName: string;
}

/**
 * Every coach on anything the printout could show, by name. The picker offers these rather than
 * the whole staff, so every name on it narrows the printout to something.
 */
export const printCoachOptions = (
  events: readonly CalendarEventResponse[],
  gamesByEvent: ReadonlyMap<string, TournamentGamesResponse>,
): PrintCoachOption[] => {
  const byId = new Map<string, string>();
  const add = (c: Pick<CalendarEventAssignee, "userId" | "displayName">) => {
    if (!byId.has(c.userId)) byId.set(c.userId, c.displayName);
  };
  for (const event of events) {
    allEventCoaches(event).forEach(add);
    gamesByEvent.get(event.id)?.games.forEach((g) => g.effectiveCoaches.forEach(add));
  }
  return [...byId.entries()]
    .map(([userId, displayName]) => ({ userId, displayName }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
};

/** "July 2026" for a whole month, otherwise "Jul 1, 2026 - Jul 15, 2026". */
export const windowLabel = (from: string, to: string): string => {
  const last = keyFromMs(dayMs(to) - MS_PER_DAY);
  const start = new Date(dayMs(from));
  const nextMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  if (from.endsWith("-01") && to === keyFromMs(nextMonth.getTime())) {
    return dateTimeFormatter("en-US", { month: "long", year: "numeric", timeZone: "UTC" }).format(
      start,
    );
  }
  return from === last ? shortDate(from) : `${shortDate(from)} - ${shortDate(last)}`;
};

/** One line saying which filters the printout reflects. */
export const printFilterSummary = (
  request: SchedulePrintRequest,
  coachNames: readonly string[],
): string => {
  const base =
    request.kind === "range"
      ? request.scope === "mine"
        ? "My events"
        : "All events"
      : { all: "All games", mine: "My games", followed: "Followed games" }[request.games];
  return coachNames.length > 0 ? `${base}. Assigned to: ${coachNames.join(", ")}` : base;
};

/** "Generated Oct 6, 2026, 3:12 PM". */
export const generatedLabel = (now: Date): string =>
  `Generated ${dateTimeFormatter("en-US", { dateStyle: "medium", timeStyle: "short" }).format(now)}`;
